import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import {
  ArrowLeft,
  RefreshCw,
  User,
  Home,
  DollarSign,
  TrendingUp,
  Calendar,
  CheckCircle,
  XCircle,
  FileText,
  Eye
} from 'lucide-react';
import API_CONSTRUCTOR from '../../../models/api-model';
import { formatCurrency, formatDate, formatDateTime } from '../../../utils/adminUtils';
import { INVESTMENT_STATUS_COLORS, INVESTMENT_STATUS_LABELS } from '../../../constants/investmentStatus';
import { toast } from 'sonner';
import ApproveCommitmentDialog from '../../../components/admin/ApproveCommitmentDialog';
import RejectCommitmentDialog from '../../../components/admin/RejectCommitmentDialog';

export const AdminInvestmentDetail = () => {
  const { commitmentId } = useParams();
  const navigate = useNavigate();
  
  const [commitment, setCommitment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const loadCommitment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const api = API_CONSTRUCTOR();
      const response = await api.get(`/commitments/${commitmentId}`);
      
      setCommitment(response.data);
    } catch (err) {
      console.error('Error loading commitment:', err);
      setError(err.response?.data?.error || 'Failed to load commitment details');
      toast.error('Failed to load commitment details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommitment();
  }, [commitmentId]);

  const handleApproveSuccess = () => {
    setShowApproveDialog(false);
    loadCommitment();
    toast.success('Commitment approved successfully');
  };

  const handleRejectSuccess = () => {
    setShowRejectDialog(false);
    loadCommitment();
    toast.success('Commitment rejected');
  };

  const getStatusColor = (status) => {
    return INVESTMENT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return INVESTMENT_STATUS_LABELS[status] || status;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-orange-600" />
          <p className="text-gray-600">Loading commitment details...</p>
        </div>
      </div>
    );
  }

  if (error || !commitment) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/admin/investments')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Investments
        </Button>
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-red-500">{error || 'Commitment not found'}</div>
            <div className="flex justify-center mt-4">
              <Button onClick={loadCommitment}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canApprove = commitment.commitment_status === 'pending';
  const canReject = ['pending', 'approved'].includes(commitment.commitment_status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <Button variant="ghost" onClick={() => navigate('/admin/investments')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Investments
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Commitment Details</h1>
          <p className="text-gray-600">Review and manage this investment commitment</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadCommitment} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status and Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Status</p>
              <Badge className={`${getStatusColor(commitment.commitment_status)} text-lg px-3 py-1`}>
                {formatStatus(commitment.commitment_status)}
              </Badge>
            </div>
            <div className="flex gap-2">
              {canApprove && (
                <Button onClick={() => setShowApproveDialog(true)} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Commitment
                </Button>
              )}
              {canReject && (
                <Button onClick={() => setShowRejectDialog(true)} variant="destructive">
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Commitment
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investor Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Investor Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-medium">{commitment.investor_name}</p>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{commitment.investor_email}</p>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-medium">{commitment.investor_phone || 'Not provided'}</p>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-gray-600">Commitment Date</p>
              <p className="font-medium">{formatDateTime(commitment.created_at)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Property Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              Property Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium">{commitment.property_address}</p>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">City</p>
                <p className="font-medium">{commitment.property_city}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">State</p>
                <p className="font-medium">{commitment.property_state}</p>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-sm text-gray-600">Zip Code</p>
              <p className="font-medium">{commitment.property_zip}</p>
            </div>
            <Separator />
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/queries/${commitment.query_id}`)}
                className="w-full"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Query Details
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Investment Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Investment Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Commitment Percentage</p>
              <p className="text-2xl font-bold text-orange-600">{commitment.commitment_percentage}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Investment Amount</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(commitment.commitment_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Expected Profit Share</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(commitment.expected_profit_share)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Total Expected Return</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(commitment.expected_total_return)}</p>
            </div>
          </div>

          <Separator className="my-6" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-2">Return Type</p>
              <p className="font-medium capitalize">{commitment.return_type || 'Standard'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Expected ROI</p>
              <p className="font-medium">{commitment.expected_roi_percentage?.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Investment Period</p>
              <p className="font-medium">{commitment.investment_period_months || 'TBD'} months</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Commitment ID</p>
              <p className="font-mono text-xs text-gray-500">{commitment.id}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes and Questions */}
      {(commitment.investor_notes || commitment.investor_questions) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Investor Notes & Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {commitment.investor_notes && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Notes:</p>
                <p className="text-gray-700 whitespace-pre-wrap">{commitment.investor_notes}</p>
              </div>
            )}
            {commitment.investor_questions && (
              <>
                {commitment.investor_notes && <Separator />}
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Questions:</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{commitment.investor_questions}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin Response */}
      {(commitment.admin_notes || commitment.rejection_reason) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Admin Response
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {commitment.admin_notes && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Approval Notes:</p>
                <p className="text-gray-700 whitespace-pre-wrap">{commitment.admin_notes}</p>
              </div>
            )}
            {commitment.rejection_reason && (
              <>
                {commitment.admin_notes && <Separator />}
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Rejection Reason:</p>
                  <p className="text-red-700 whitespace-pre-wrap">{commitment.rejection_reason}</p>
                </div>
              </>
            )}
            {commitment.approved_at && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-600">Approved At</p>
                  <p className="font-medium">{formatDateTime(commitment.approved_at)}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <ApproveCommitmentDialog
        open={showApproveDialog}
        onClose={() => setShowApproveDialog(false)}
        onSuccess={handleApproveSuccess}
        commitmentId={commitmentId}
      />

      <RejectCommitmentDialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onSuccess={handleRejectSuccess}
        commitmentId={commitmentId}
      />
    </div>
  );
};

export default AdminInvestmentDetail;
